import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { api } from '../../api/client'
import type { Delivery, DeliveryWeek } from '../../api/types'
import { useStores } from '../../app/StoreContext'
import { Badge, Button, Empty, ErrorBox, Spinner, formatDate, startOfWeek, toIsoDate } from '../../ui/primitives'
import { useAsync } from './useAsync'
import {
  STATUS_ORDER,
  deliveryTitle,
  documentFlag,
  matchesSupplier,
  slotLabel,
  statusLabel,
} from './labels'

// Agenda settimanale delle consegne.
//
// Ricalca il "Planning" che gli utenti usano oggi: una colonna per giorno da lunedì a sabato,
// gli arretrati a sinistra e ciò che arriverà dopo a destra. La settimana intera si legge senza
// scorrere, perché la domanda vera del negozio è "cosa mi arriva oggi e cosa era già arrivato".

const DAY_NAMES = ['lunedì', 'martedì', 'mercoledì', 'giovedì', 'venerdì', 'sabato', 'domenica']

/** Le date dell'API sono 'yyyy-MM-dd': aggiungo l'ora per non farle scivolare di fuso. */
function parseIsoDate(iso: string) {
  return new Date(`${iso.slice(0, 10)}T00:00:00`)
}

function dayName(iso: string) {
  const date = parseIsoDate(iso)
  return DAY_NAMES[(date.getDay() + 6) % 7]
}

function shiftDays(date: Date, days: number) {
  const copy = new Date(date)
  copy.setDate(copy.getDate() + days)
  return copy
}

export function PlanningPage() {
  const navigate = useNavigate()
  const { selected, stores, storeFilter, storesReady } = useStores()

  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date()))
  const [supplierFilter, setSupplierFilter] = useState('')
  const [includeCompleted, setIncludeCompleted] = useState(false)
  const [onlyOverdue, setOnlyOverdue] = useState(false)

  const weekStartIso = toIsoDate(weekStart)
  const todayIso = toIsoDate(new Date())

  // Il filtro fornitore è a testo libero perché è così che lo si pensa ("Royal Canin", non
  // "000355"). Il server però filtra per codice esatto: quando l'utente scrive proprio un
  // codice glielo passiamo — sono meno dati in arrivo — altrimenti filtriamo qui sul nome.
  const typed = supplierFilter.trim()
  const supplierCode = /^\d{3,}$/.test(typed) ? typed : undefined
  const localFilter = supplierCode ? '' : typed

  const storeKey = selected.join(',')

  const week = useAsync<DeliveryWeek>(
    () =>
      api.get<DeliveryWeek>('/deliveries/week', {
        storeCode: storeFilter,
        weekStart: weekStartIso,
        supplierCode,
        includeCompleted,
      }),
    [storeKey, weekStartIso, supplierCode, includeCompleted],
    // Prima che l'elenco dei punti vendita sia noto il filtro non è affidabile: si aspetta.
    storesReady,
  )

  // I filtri che il server non conosce (nome fornitore, "solo in ritardo") si applicano qui,
  // una volta sola, così tutte le colonne restano coerenti fra loro.
  const filtered = useMemo(() => {
    const keep = (d: Delivery) => matchesSupplier(d, localFilter) && (!onlyOverdue || d.isOverdue)
    if (!week.data) return undefined
    return {
      ...week.data,
      days: week.data.days.map((day) => ({ ...day, deliveries: day.deliveries.filter(keep) })),
      earlier: week.data.earlier.filter(keep),
      later: week.data.later.filter(keep),
    }
  }, [week.data, localFilter, onlyOverdue])

  const total = useMemo(() => {
    if (!filtered) return 0
    return (
      filtered.earlier.length +
      filtered.later.length +
      filtered.days.reduce((sum, day) => sum + day.deliveries.length, 0)
    )
  }, [filtered])

  const weekEnd = formatDate(toIsoDate(shiftDays(weekStart, 5)))

  const chip = (delivery: Delivery) => {
    const flag = documentFlag(delivery.documentStatus)
    const slot = slotLabel(delivery.expectedTimeSlot)

    return (
      <button
        key={delivery.id}
        type="button"
        className={`delivery-chip status-${delivery.status}${delivery.isOverdue ? ' overdue' : ''}`}
        onClick={() => navigate(`/planning/${encodeURIComponent(delivery.id)}`)}
        title={`${deliveryTitle(delivery)} — ${statusLabel(delivery.status)}`}
      >
        <span className="name">{deliveryTitle(delivery)}</span>
        <span className="meta">
          {delivery.orderNumber && <span>ord. {delivery.orderNumber}</span>}
          <span>{statusLabel(delivery.status)}</span>
          {slot && <span>{slot}</span>}
          {/* Il codice del punto vendita serve solo quando in agenda può comparirne più
              d'uno: per chi ne ha uno solo è rumore. */}
          {stores.length > 1 && selected.length !== 1 && <span>pv {delivery.storeCode}</span>}
        </span>
        {(flag || delivery.isOverdue) && (
          <span className="meta">
            {delivery.isOverdue && <Badge tone="danger">In ritardo</Badge>}
            {flag && <Badge tone={flag.tone}>{flag.label}</Badge>}
          </span>
        )}
      </button>
    )
  }

  const column = (
    key: string,
    title: string,
    subtitle: string,
    deliveries: Delivery[],
    options: { aside?: boolean; today?: boolean; emptyText: string },
  ) => (
    <div
      key={key}
      className={`week-col${options.aside ? ' aside' : ''}${options.today ? ' today' : ''}`}
    >
      <div className="week-col-head">
        <div className="day">{title}</div>
        <div className="date">{subtitle}</div>
      </div>
      <div className="week-col-body">
        {deliveries.map(chip)}
        {deliveries.length === 0 && <div className="week-col-empty">{options.emptyText}</div>}
      </div>
    </div>
  )

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Planning consegne</h1>
          <div className="muted">
            Settimana dal {formatDate(weekStartIso)} al {weekEnd}
          </div>
        </div>

        <div className="page-tools">
          <Button onClick={() => setWeekStart((w) => shiftDays(w, -7))} title="Settimana precedente">
            « Precedente
          </Button>
          <Button onClick={() => setWeekStart(startOfWeek(new Date()))} title="Torna alla settimana corrente">
            Oggi
          </Button>
          <Button onClick={() => setWeekStart((w) => shiftDays(w, 7))} title="Settimana successiva">
            Successiva »
          </Button>

          <input
            className="planning-filter"
            placeholder="Fornitore: nome o codice"
            value={supplierFilter}
            onChange={(e) => setSupplierFilter(e.target.value)}
          />

          <label className="inline">
            <input
              type="checkbox"
              checked={includeCompleted}
              onChange={(e) => setIncludeCompleted(e.target.checked)}
            />
            <span>Includi completate</span>
          </label>

          {week.data && week.data.overdueCount > 0 && (
            <Button
              variant={onlyOverdue ? 'danger' : 'default'}
              onClick={() => setOnlyOverdue((v) => !v)}
              title={
                onlyOverdue ?
                  'Mostra di nuovo tutte le consegne'
                : 'Mostra solo le consegne in ritardo'
              }
            >
              {week.data.overdueCount} in ritardo
            </Button>
          )}
          {week.data && week.data.overdueCount === 0 && <Badge tone="ok">Nessun ritardo</Badge>}
        </div>
      </div>

      {/* Con un solo punto vendita autorizzato non c'è nulla da restringere: l'avviso
          suggerirebbe un selettore che non esiste. */}
      {selected.length === 0 && stores.length > 1 && (
        <div className="notice notice-info">
          <strong>Stai vedendo tutti i punti vendita.</strong>
          <p>
            L'agenda diventa molto più leggibile restringendo la vista dal selettore in alto a
            destra.
          </p>
        </div>
      )}

      {onlyOverdue && (
        <div className="notice notice-warn">
          <strong>Stai vedendo solo le consegne in ritardo.</strong>
          <p>Premi di nuovo il contatore rosso in alto per tornare all'agenda completa.</p>
        </div>
      )}

      {week.loading && <Spinner label="Carico l'agenda della settimana…" />}
      {week.error !== undefined && <ErrorBox error={week.error} onRetry={week.reload} />}

      {filtered && !week.loading && total === 0 && (
        <Empty>
          Nessuna consegna in questa settimana
          {localFilter && ` per un fornitore che contenga «${localFilter}»`}
          {supplierCode && ` per il fornitore ${supplierCode}`}
          {onlyOverdue && ' fra quelle in ritardo'}
          {!includeCompleted && ', escludendo quelle già completate'}.
          <div className="muted">
            Prova a togliere i filtri, ad attivare «Includi completate» o a spostarti su un'altra
            settimana con i pulsanti in alto.
          </div>
        </Empty>
      )}

      {filtered && !week.loading && total > 0 && (
        <>
          <div className="week">
            {column(
              'earlier',
              'Precedenti',
              'non ancora concluse',
              filtered.earlier,
              { aside: true, emptyText: 'Nessun arretrato.' },
            )}

            {filtered.days.map((day) =>
              column(
                day.date,
                dayName(day.date),
                formatDate(day.date),
                day.deliveries,
                { today: day.date.slice(0, 10) === todayIso, emptyText: 'Nessuna consegna.' },
              ),
            )}

            {column('later', 'Future', 'dopo questa settimana', filtered.later, {
              aside: true,
              emptyText: 'Nulla in programma.',
            })}
          </div>

          <div className="legend">
            <span>Stato:</span>
            {STATUS_ORDER.map((status) => (
              <span className="legend-item" key={status}>
                <span className={`legend-swatch status-${status}`} aria-hidden />
                {statusLabel(status)}
              </span>
            ))}
            <span className="legend-item">
              <span className="legend-swatch overdue" aria-hidden />
              In ritardo (sfondo rosato)
            </span>
          </div>
        </>
      )}
    </>
  )
}
